<script setup>
import { ref } from "vue";
import { useGlobalStore } from "@stores/global.js";
import { useShopifyStore } from "@stores/shopify.js";
import Close from "@components/icons/Close.vue";
import Trash from "@components/icons/Trash.vue";
import Plus from "@components/icons/Plus.vue";
import Minus from "@components/icons/Minus.vue";
import Button from "@components/global/Button.vue";
import { prefersReducedMotion } from "@utils/prefersReducedMotion";
import gsap from "gsap";

const global = useGlobalStore();
const shopify = useShopifyStore();
const cartRef = ref(null);

function closeCart() {
	global.toggleCart(false);
}

function updateQuantity(item, newQuantity) {
	if (newQuantity <= 0) {
		removeItem(item.id);
	} else {
		shopify.updateCart({
			id: item.id,
			quantity: newQuantity,
		});
	}
}

function removeItem(itemId) {
	shopify.removeFromCart(itemId);
}

function checkout() {
	if (shopify.cart.checkoutURL) {
		window.location.href = shopify.cart.checkoutURL;
	}
}

const onEnter = (el, done) => {
	if (prefersReducedMotion()) return done();

	const tl = gsap.timeline({
		onComplete: () => {
			done();
		},
	});

	gsap.set(el, {
		opacity: 0,
	});

	gsap.set(cartRef.value, {
		xPercent: 100,
	});

	tl.to(el, {
		opacity: 1,
		duration: 0.22,
		ease: "power2.inOut",
	});

	tl.to(
		cartRef.value,
		{
			xPercent: 0,
			duration: 0.22,
			ease: "power2.inOut",
		},
		"<",
	);
};

const onLeave = (el, done) => {
	if (prefersReducedMotion()) return done();

	const tl = gsap.timeline({
		onComplete: () => {
			done();
		},
	});

	tl.to(el, {
		opacity: 0,
		duration: 0.22,
		ease: "power2.in",
	});

	tl.to(cartRef.value, {
		xPercent: 100,
		duration: 0.22,
		ease: "power2.in",
	});
};
</script>

<template>
	<transition appear :css="false" @enter="onEnter" @leave="onLeave">
		<div
			class="fixed inset-0 z-50 flex size-full justify-end bg-black/50 text-[var(--theme-surface)]"
			v-if="global.cart"
		>
			<div
				class="bg-theme flex h-full w-full flex-col sm:max-w-400"
				ref="cartRef"
				v-click-outside="closeCart"
			>
				<div
					class="flex items-center justify-between border-b border-[var(--theme-surface)]/10 px-16 py-16 md:px-24 md:py-20"
				>
					<p class="h7 font-primary">
						Your cart ({{ shopify.cartItemCount }})
					</p>
					<button @click="closeCart" aria-label="Close cart">
						<Close />
					</button>
				</div>
				<div class="flex grow flex-col gap-12 overflow-y-auto p-16 md:p-24">
					<div
						v-if="shopify.isCartEmpty"
						class="flex flex-col items-center justify-center py-32"
					>
						<p class="h8 font-primary text-center">Your cart is empty</p>
					</div>
					<div
						v-for="item in shopify.cartItems"
						:key="item.id"
						class="flex items-center justify-between"
					>
						<div class="flex items-center gap-16 md:gap-24">
							<img
								v-if="item.image"
								:src="item.image"
								:alt="item.image_alt || item.title"
								class="shink-0 aspect-square size-64 rounded-lg object-cover md:size-80 lg:size-104"
							/>
							<div class="flex flex-col gap-8 md:gap-12">
								<p class="h8 font-primary-condensed">
									{{ item.title }}
								</p>
								<p class="h8 font-primary">{{ item.price }}</p>
								<div class="flex items-center gap-4">
									<button
										@click="updateQuantity(item, item.quantity - 1)"
										class="size-20items-center flex justify-center"
									>
										<span class="sr-only">Decrease quantity</span>
										<Minus class="size-14" />
									</button>
									<span
										class="h8 font-primary min-w-20 text-center text-sm"
									>
										{{ item.quantity }}
									</span>
									<button
										@click="updateQuantity(item, item.quantity + 1)"
										class="flex size-20 items-center justify-center"
									>
										<span class="sr-only">Increase quantity</span>
										<Plus class="size-14" />
									</button>
								</div>
							</div>
						</div>
						<button
							@click="removeItem(item.id)"
							class="transition-opacity hover:opacity-75"
						>
							<span class="sr-only">Remove item</span>
							<Trash />
						</button>
					</div>
				</div>
				<div
					v-if="!shopify.isCartEmpty"
					class="flex flex-col gap-24 p-16 md:gap-32 md:p-24"
				>
					<div class="flex flex-col gap-16 md:gap-24">
						<div class="flex items-center justify-between">
							<p class="h8 font-primary">Subtotal</p>
							<p class="h8 font-primary-condensed">
								{{ shopify.cartSubtotal }}
							</p>
						</div>
						<div class="flex items-center justify-between">
							<p class="h8 font-primary">Delivery</p>
							<p class="h8 font-primary-condensed">
								Calculated at checkout
							</p>
						</div>
						<div
							class="flex items-center justify-between border-t border-[var(--theme-surface)]/10 pt-16 md:pt-24"
						>
							<p class="h8 font-primary">Total</p>
							<p class="h8 font-primary-condensed">
								{{ shopify.cartTotal }}
							</p>
						</div>
					</div>
					<Button @click="checkout" :disabled="shopify.cartLoading">
						{{ shopify.cartLoading ? "Loading..." : "Checkout" }}
					</Button>
				</div>
			</div>
		</div>
	</transition>
</template>
